import { Loader2, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { UploadState } from '@/hooks/usePhotoUpload';

interface UploadProgressProps {
  uploadState: UploadState;
  onRetry?: () => void;
  onCancel?: () => void;
}

export default function UploadProgress({ uploadState, onRetry, onCancel }: UploadProgressProps) {
  const { isUploading, progress, error, retryCount, maxRetries } = uploadState;

  if (!isUploading && !progress && !error) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      {isUploading && progress && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Uploading...</span>
            <span className="text-gray-600">{progress.percentage}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
            </span>
            {retryCount > 0 && (
              <span>Retry {retryCount}/{maxRetries}</span>
            )}
          </div>
        </div>
      )}

      {/* Upload Status */}
      {isUploading && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-700">
              {retryCount > 0 ? 'Retrying upload...' : 'Uploading photo...'}
            </p>
            {progress && (
              <p className="text-xs text-blue-600">
                {progress.percentage}% complete
              </p>
            )}
          </div>
          {onCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="text-xs"
            >
              Cancel
            </Button>
          )}
        </div>
      )}

      {/* Success State */}
      {!isUploading && progress && progress.percentage === 100 && !error && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-700">
            Photo uploaded successfully!
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">Upload Failed</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          </div>
          
          {onRetry && retryCount < maxRetries && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Retry Upload
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Utility function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}